import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/cn';

// Document references like SI-2026-0042, CUST-0011 (no year), PE-2026-0001…
// Two shapes: PREFIX-YYYY-NNNN (transactional) and PREFIX-NNNN (master).
const DOC_REF_TRX_RE   = /\b([A-Z]{2,5})-(\d{4})-(\d{4,6})\b/g;
const DOC_REF_MASTER_RE = /\b(CUST|SUPP|ITEM|EMP|LEAD|PROJ|ISS|BOM|WO|ASSET)-(\d{3,6})\b/g;

const DOC_PREFIX_TO_PATH: Record<string, { module: string; slug: string }> = {
  SI:    { module: '/accounting',    slug: 'sales-invoices' },
  PI:    { module: '/accounting',    slug: 'purchase-invoices' },
  JE:    { module: '/accounting',    slug: 'journal-entries' },
  PE:    { module: '/accounting',    slug: 'payment-entries' },
  PO:    { module: '/accounting',    slug: 'purchase-orders' },
  SO:    { module: '/accounting',    slug: 'sales-orders' },
  MR:    { module: '/stock',         slug: 'material-requests' },
  DN:    { module: '/stock',         slug: 'delivery-notes' },
  PR:    { module: '/stock',         slug: 'purchase-receipts' },
  SE:    { module: '/stock',         slug: 'stock-entries' },
  CUST:  { module: '/accounting',    slug: 'customers' },
  SUPP:  { module: '/accounting',    slug: 'suppliers' },
  ITEM:  { module: '/accounting',    slug: 'items' },
  EMP:   { module: '/hr',            slug: 'employees' },
  PROJ:  { module: '/projects',      slug: 'projects' },
  ASSET: { module: '/assets',        slug: 'assets' },
};

const INTERNAL_LINK_SCHEME = 'logica-doc:';

// Wraps any doc reference inside the source as a markdown link with a
// custom scheme. The renderer's `a` override intercepts that scheme and
// turns the click into a router navigate. Code fences and inline code
// are skipped so refs that appear in JSON tool output stay literal.
function linkifyDocRefs(src: string): string {
  const parts: string[] = [];
  let i = 0;
  // Walk the string and split on fenced/inline code so we never mutate code.
  // ``` ``` fences and `inline` spans both get skipped.
  const codeRe = /```[\s\S]*?```|`[^`\n]*`/g;
  let m: RegExpExecArray | null;
  while ((m = codeRe.exec(src)) !== null) {
    if (m.index > i) parts.push(rewrite(src.slice(i, m.index)));
    parts.push(m[0]);
    i = m.index + m[0].length;
  }
  if (i < src.length) parts.push(rewrite(src.slice(i)));
  return parts.join('');

  function rewrite(chunk: string): string {
    // Master refs first so we don't accidentally over-match a numeric tail.
    return chunk
      .replace(DOC_REF_TRX_RE, (full, prefix) => {
        const map = DOC_PREFIX_TO_PATH[prefix as string];
        if (!map) return full;
        const href = `${INTERNAL_LINK_SCHEME}${map.module}/${map.slug}?name=${encodeURIComponent(full)}`;
        return `[${full}](${href})`;
      })
      .replace(DOC_REF_MASTER_RE, (full, prefix) => {
        const map = DOC_PREFIX_TO_PATH[prefix as string];
        if (!map) return full;
        const href = `${INTERNAL_LINK_SCHEME}${map.module}/${map.slug}?name=${encodeURIComponent(full)}`;
        return `[${full}](${href})`;
      });
  }
}

interface Props {
  content: string;
  className?: string;
}

export function MarkdownMessage({ content, className }: Props) {
  const navigate = useNavigate();
  const source = useMemo(() => linkifyDocRefs(content), [content]);

  return (
    <div
      className={cn(
        // Base typography for the message body. Element overrides below
        // tune individual tags — keep this tight so it matches the rest
        // of the chat (text-body-sm on a surface bg).
        'text-body-sm text-charcoal leading-relaxed break-words',
        // Vertical rhythm between sibling block elements.
        '[&>*+*]:mt-2',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="whitespace-pre-wrap">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold text-charcoal">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          del: ({ children }) => <del className="text-stone line-through">{children}</del>,

          h1: ({ children }) => <h1 className="text-body font-semibold text-charcoal mt-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-body font-semibold text-charcoal mt-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-body-sm font-semibold text-charcoal mt-2">{children}</h3>,
          h4: ({ children }) => <h4 className="text-body-sm font-semibold text-charcoal">{children}</h4>,
          h5: ({ children }) => <h5 className="text-caption font-semibold uppercase tracking-wide text-stone">{children}</h5>,
          h6: ({ children }) => <h6 className="text-caption font-semibold uppercase tracking-wide text-stone">{children}</h6>,

          ul: ({ children }) => <ul className="list-disc pl-5 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="marker:text-stone">{children}</li>,

          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-divider pl-3 text-stone italic">
              {children}
            </blockquote>
          ),

          hr: () => <hr className="border-divider my-3" />,

          a: ({ href, children }) => {
            const url = href ?? '';
            if (url.startsWith(INTERNAL_LINK_SCHEME)) {
              const to = url.slice(INTERNAL_LINK_SCHEME.length);
              return (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); void navigate({ to: to as never }); }}
                  className="text-accent hover:underline font-mono"
                >
                  {children}
                </button>
              );
            }
            const external = /^https?:\/\//.test(url);
            return (
              <a
                href={url}
                target={external ? '_blank' : undefined}
                rel={external ? 'noreferrer noopener' : undefined}
                className="text-accent hover:underline"
              >
                {children}
              </a>
            );
          },

          code: ({ className: cls, children, ...rest }) => {
            // react-markdown distinguishes inline vs block via the parent
            // element; here we infer from the language class. Inline code
            // has no language; block code arrives wrapped in <pre>.
            const isBlock = typeof cls === 'string' && cls.startsWith('language-');
            if (isBlock) {
              return (
                <code className={cn('font-mono text-caption', cls)} {...rest}>
                  {children}
                </code>
              );
            }
            return (
              <code
                className="font-mono text-[0.85em] bg-divider/40 text-charcoal rounded px-1 py-0.5"
                {...rest}
              >
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="overflow-x-auto rounded-md bg-divider/40 text-charcoal p-2 text-caption font-mono">
              {children}
            </pre>
          ),

          // GFM tables. Keep them scroll-on-overflow so the chat bubble
          // doesn't force the whole panel wider.
          table: ({ children }) => (
            <div className="overflow-x-auto rounded-md border border-divider">
              <table className="w-full border-collapse text-caption">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-divider/40">{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => <tr className="border-b border-divider last:border-b-0">{children}</tr>,
          th: ({ children, style }) => (
            <th
              className="px-2 py-1 text-left font-semibold text-charcoal"
              style={style}
            >
              {children}
            </th>
          ),
          td: ({ children, style }) => (
            <td className="px-2 py-1 align-top" style={style}>
              {children}
            </td>
          ),
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
